import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results"]
  static values = { expectedHeaders: Array }

  clear() {
    this.inputTarget.value = ""
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
  }

  validate() {
    const raw = this.inputTarget.value.trim()
    if (!raw) {
      this.render({ summary: "Paste a CSV to validate.", issues: [] })
      return
    }

    const rows = this.parse(raw)
    if (rows.length === 0) {
      this.render({ summary: "No rows detected.", issues: [] })
      return
    }

    const issues = []
    const header = rows[0].map((h) => h.trim())
    const expected = this.expectedHeadersValue

    expected.forEach((col, i) => {
      if ((header[i] || "").toLowerCase() !== col.toLowerCase()) {
        issues.push({
          severity: "error",
          line: 1,
          message: `Column ${i + 1} should be "${col}", got "${header[i] || "(missing)"}"`
        })
      }
    })
    if (header.length !== expected.length) {
      issues.push({
        severity: "error",
        line: 1,
        message: `Expected ${expected.length} columns, got ${header.length}.`
      })
    }

    const idx = Object.fromEntries(expected.map((c, i) => [c, i]))

    for (let r = 1; r < rows.length; r++) {
      const row = rows[r]
      if (row.length === 1 && row[0] === "") continue
      const ln = r + 1

      const acquired = (row[idx["Date Acquired"]] || "").trim()
      const sold = (row[idx["Date Sold"]] || "").trim()
      if (acquired && sold && acquired.toUpperCase() !== "VARIOUS") {
        const a = Date.parse(acquired)
        const s = Date.parse(sold)
        if (!isNaN(a) && !isNaN(s) && a > s) {
          issues.push({ severity: "error", line: ln, message: `Date Sold (${sold}) is before Date Acquired (${acquired}).` })
        }
      }

      const cost = (row[idx["Cost Basis"]] || "").trim()
      if (cost === "" || cost === "0.00" || cost === "0") {
        issues.push({ severity: "warning", line: ln, message: `Cost Basis is ${cost === "" ? "empty" : "zero"} — noncovered lot or missing data. Box B/E may need Code B adjustment.` })
      }

      const wash = parseFloat(row[idx["Wash Sale Loss Disallowed"]] || "")
      const securityType = (row[idx["Security Type"]] || "").toLowerCase()
      if (!isNaN(wash) && wash > 0) {
        if (!securityType.includes("short") && !securityType.includes("long")) {
          issues.push({ severity: "error", line: ln, message: `Wash Sale row missing Security Type (Short-term/Long-term needed to pick TXF code 322 or 324).` })
        }
      }

      const proceeds = parseFloat(row[idx["Proceeds"]] || "")
      const costF = parseFloat(cost)
      const gainLoss = parseFloat(row[idx["Gain/Loss"]] || "")
      if (!isNaN(proceeds) && !isNaN(costF) && !isNaN(gainLoss)) {
        const diff = Math.abs(proceeds - costF - gainLoss)
        if (diff > 0.02) {
          issues.push({ severity: "warning", line: ln, message: `Gain/Loss (${gainLoss}) doesn't match Proceeds − Cost Basis (${(proceeds - costF).toFixed(2)}). Off by $${diff.toFixed(2)} — may be wash-sale adjustment or a data error.` })
        }
      }
    }

    const errors = issues.filter((i) => i.severity === "error").length
    const warnings = issues.filter((i) => i.severity === "warning").length
    const dataRows = rows.length - 1
    const summary = issues.length === 0
      ? `Looks clean — ${dataRows} row${dataRows === 1 ? "" : "s"} validated with zero issues.`
      : `${errors} error${errors === 1 ? "" : "s"}, ${warnings} warning${warnings === 1 ? "" : "s"} across ${dataRows} row${dataRows === 1 ? "" : "s"}.`

    this.render({ summary, issues })
  }

  render({ summary, issues }) {
    const errorsExist = issues.some((i) => i.severity === "error")
    const warningsOnly = !errorsExist && issues.some((i) => i.severity === "warning")
    const banner = errorsExist
      ? { border: "border-rose-200", bg: "bg-rose-50", icon: "text-rose-500", text: "text-rose-900" }
      : warningsOnly
      ? { border: "border-amber-200", bg: "bg-amber-50", icon: "text-amber-500", text: "text-amber-900" }
      : { border: "border-emerald-200", bg: "bg-emerald-50", icon: "text-emerald-500", text: "text-emerald-900" }

    const rows = issues.map((i) => {
      const color = i.severity === "error" ? "text-rose-700" : "text-amber-700"
      const label = i.severity === "error" ? "ERROR" : "WARN"
      return `<li class="py-2.5 px-4 border-b border-slate-100 last:border-b-0 text-sm flex gap-3">
        <span class="${color} font-mono text-xs shrink-0 w-14">${label}</span>
        <span class="text-slate-500 font-mono text-xs shrink-0 w-14">line ${i.line}</span>
        <span class="text-slate-700">${this.escape(i.message)}</span>
      </li>`
    }).join("")

    this.resultsTarget.innerHTML = `
      <div class="rounded-2xl border-2 ${banner.border} ${banner.bg} p-5 mb-4 flex items-start gap-3">
        <svg class="w-5 h-5 ${banner.icon} mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
        <p class="text-sm font-medium ${banner.text}">${this.escape(summary)}</p>
      </div>
      ${issues.length > 0 ? `<div class="bg-white border border-slate-200 rounded-2xl overflow-hidden"><ul class="divide-y divide-slate-100">${rows}</ul></div>` : ""}
    `
    this.resultsTarget.classList.remove("hidden")
  }

  parse(text) {
    const lines = text.split(/\r?\n/)
    return lines.map((line) => this.parseLine(line))
  }

  parseLine(line) {
    const out = []
    let cur = ""
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
      const c = line[i]
      if (c === '"' && line[i + 1] === '"' && inQuotes) {
        cur += '"'
        i++
      } else if (c === '"') {
        inQuotes = !inQuotes
      } else if (c === "," && !inQuotes) {
        out.push(cur)
        cur = ""
      } else {
        cur += c
      }
    }
    out.push(cur)
    return out
  }

  escape(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }
}
